import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Option interface for MultiSelectWithSearch component
 */
export interface FilterSelectOption {
  /** Option value */
  value: string;
  /** Option display label */
  label: string;
  /** Whether option is disabled */
  disabled?: boolean;
}

/**
 * Props for the MultiSelectWithSearch component
 */
interface MultiSelectWithSearchProps {
  /** Array of select options */
  options: FilterSelectOption[];
  /** Selected values array */
  value: string[];
  /** Callback fired when values change */
  onChange: (values: string[]) => void;
  /** Placeholder text. Defaults to "Select". */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * MultiSelectWithSearch component displays a multi-select dropdown with search
 * functionality. Shows selected values as badges and allows filtering options by query.
 * Built on Popover and Button components.
 * 
 * @component
 * @param {MultiSelectWithSearchProps} props - Component props
 * @param {FilterSelectOption[]} props.options - Select options
 * @param {string[]} props.value - Selected values
 * @param {(values: string[]) => void} props.onChange - Change callback
 * @param {string} [props.placeholder="Select"] - Placeholder text
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Multi-select dropdown with search
 * 
 * @example
 * ```tsx
 * <MultiSelectWithSearch
 *   options={options}
 *   value={selectedValues}
 *   onChange={handleChange}
 *   placeholder="Select values"
 * />
 * ```
 */
export const MultiSelectWithSearch: React.FC<MultiSelectWithSearchProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const uniqueOptions = React.useMemo(() => {
    const map = new Map();
    options.forEach((opt) => {
      if (!map.has(opt.value)) {
        map.set(opt.value, opt);
      }
    });
    return Array.from(map.values());
  }, [options]);

  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const removeChip = (val: string) => {
    onChange(value.filter((v) => v !== val));
  };

  const filteredOptions = uniqueOptions.filter((opt) =>
    opt.label.toLowerCase().includes(query.toLowerCase())
  );

  const selectedOptions = uniqueOptions.filter((opt) =>
    value.includes(opt.value)
  );

  return (
    <div className="text-gray-700">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-between p-2 h-auto min-h-[44px] relative flex items-center gap-2",
              className
            )}
          >
            <div className="flex flex-wrap gap-1 max-h-[48px] overflow-y-auto pr-6 flex-1">
              {selectedOptions.length > 0 ? (
                selectedOptions.map((opt) => (
                  <Badge
                    key={opt.value}
                    variant="outline"
                    className="flex items-center gap-1 text-gray-700 rounded-full"
                  >
                    {opt.label}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeChip(opt.value);
                      }}
                      className="p-0 m-0 flex items-center"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))
              ) : (
                <span className="text-gray-700">{placeholder}</span>
              )}
            </div>

            <ChevronDown className="h-4 w-4 opacity-50 absolute right-2" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="p-0 w-[var(--radix-popover-trigger-width)]"
        >
          <div className="p-2 border-b">
            <Input
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8"
            />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <p className="text-sm text-center text-gray-700 py-4">
                No results
              </p>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "text-sm text-gray-700 flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50",
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => !option.disabled && toggleValue(option.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value.includes(option.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                  />
                  <span>{option.label}</span>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
